/**
 * Set an attribute with a a value on a particular element
 *
 * @param {element} elem  the element to set the attribute on
 * @param {string} attribute  the attribute to put a value in
 * @param {string} val  the value to add to the attribute
 */
export function setElemAttribute (elem, attribute, val) {
    if (elem.getAttribute(attribute) !== `${val}`) {
        elem.setAttribute(attribute, val);
    }
}

/**
 * Work out the co-ordinates/position on screen of an element
 * @param  {element} elem  the element to find
 *
 * @return {object}  the position of the element in an object
 */
export function getCoordinates (elem) {
    const bounds = elem.getBoundingClientRect();

    return { left: bounds.left, top: bounds.top };
}

/**
 * Take an array of elements and order them based on top and left position
 * @param  {array} elemsArray  the array of elements to sort
 *
 * @return {array}  the sorted array
 */
export function sortElementsByCoordinates (elemsArray) {
    return elemsArray.sort(function (a, b) {
        const aOffset = getCoordinates(a);
        const bOffset = getCoordinates(b);

        if (aOffset.top === bOffset.top) {
            if (aOffset.left === bOffset.left) {
                return 0;
            }

            return (aOffset.left < bOffset.left) ? -1 : 1;
        }

        return (aOffset.top < bOffset.top) ? -1 : 1;
    });
}

export function selectElementText (elem) {
    let range;
    let selection;

    if (window.getSelection) {
        selection = window.getSelection();
        range = document.createRange();
        range.selectNodeContents(elem);
        selection.removeAllRanges();
        selection.addRange(range);
    } else if (document.body.createTextRange) {
        range = document.body.createTextRange();
        range.moveToElementText(elem);
        range.select();
    }
}

export function addEventListenerList (list, event, callback) {
    for (let i = 0; i < list.length; i++) {
        list[i].addEventListener(event, callback, false);
    }
}

/*
 * setCookie
 * Stores data in a cookie.
 *
 * @param  {object}  properties  Key/value pairs to define cookie:
 * - name
 * - value
 * - days (number of days until it expires, leave blank for session)
 * - path (defaults to '/')
 *
 * @return  {void}  was the cookie set successfully?
 */
export function setCookie (properties) {
    const cookieName = properties.name;
    const cookieValue = (properties.value ? properties.value : '');
    const cookiePath = `;path=${properties.path ? properties.path : '/'}`;
    const cookieDomain = '.plus.net';
    let cookieExpires = '';

    if (properties.expires) {
        const date = new Date();

        date.setTime(date.getTime() + properties.expires);

        cookieExpires += `;expires=${date.toString()}`;
    }

    const cookieNameAndValue = `${cookieName}=${cookieValue}`;

    document.cookie =
        cookieNameAndValue
        + cookieExpires
        + cookiePath
        + cookieDomain;
}

export function getCookie (name) {
    const cookieRegEx = new RegExp(`(?:^|;\\s*)(?:${name})=(.*?)(?:;|$)`, 'g');
    const match = cookieRegEx.exec(document.cookie);

    if (match !== null) {
        return match[1];
    }

    return false;
}

/**
 * Make a list of elements tabbable by keyboard
 * @param  {Elements} tabElems    the elements you want to make tabbable
 */
export function makeTabbable (tabElems) {
    let tabs;

    if (Object.prototype.isPrototypeOf.call(NodeList.prototype, tabElems)) {
        tabs = Array.prototype.slice.call(tabElems);
    } else {
        tabs = [tabElems];
    }

    tabs.forEach(tabElem => {
        tabElem.removeAttribute('tabindex');
    });
}

/**
 * Make a list of elements untabbable by keyboard
 * @param  {Elements} unTabElems    the elements you want to make untabbable
 */
export function makeUntabbable (unTabElems) {
    let unTabs;

    if (Object.prototype.isPrototypeOf.call(NodeList.prototype, unTabElems)) {
        unTabs = Array.prototype.slice.call(unTabElems);
    } else {
        unTabs = [unTabElems];
    }

    unTabs.forEach(unTabElem => {
        unTabElem.setAttribute('tabindex', '-1');
    });
}
